import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { SmartImg } from "@/components/SmartImg";
import {
  listApplications,
  updateApplication,
  listVisagistApplications,
  updateVisagistApplication,
  type ApplicationStatus,
} from "@/lib/admin-api";

export const Route = createFileRoute("/admin/applications")({
  component: ApplicationsPage,
});

type App = Awaited<ReturnType<typeof listApplications>>[number];
type Kind = "photographer" | "visagist";

const statusLabels: Record<ApplicationStatus | "all", string> = {
  all: "Alle",
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
};

function ApplicationsPage() {
  const [kind, setKind] = useState<Kind>("photographer");
  const [filter, setFilter] = useState<ApplicationStatus | "all">("pending");
  const [items, setItems] = useState<App[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<App | null>(null);

  async function load() {
    setLoading(true);
    try {
      const data = kind === "visagist"
        ? await listVisagistApplications(filter)
        : await listApplications(filter);
      setItems(data as App[]);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, kind]);

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow text-muted-foreground">Moderation</div>
          <h1 className="serif text-4xl md:text-5xl mt-3">Bewerbungen</h1>
          <div className="mt-4 inline-flex border border-border eyebrow">
            <button type="button" onClick={() => setKind("photographer")} className={`px-4 py-2 ${kind === "photographer" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>
              Fotograf:innen
            </button>
            <button type="button" onClick={() => setKind("visagist")} className={`px-4 py-2 border-l border-border ${kind === "visagist" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>
              Visagist:innen
            </button>
          </div>
        </div>
        <div className="flex gap-2 eyebrow">
          {(Object.keys(statusLabels) as Array<ApplicationStatus | "all">).map((k) => (
            <button key={k} onClick={() => setFilter(k)}
              className={`px-3 py-1 border ${filter === k ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {statusLabels[k]}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 border border-border divide-y divide-border">
        {loading && <div className="p-6 text-sm text-muted-foreground">Lädt …</div>}
        {!loading && items.length === 0 && <div className="p-6 text-sm text-muted-foreground">Keine Einträge.</div>}
        {items.map((a) => (
          <button key={a.id} onClick={() => setOpen(a)} className="w-full text-left p-4 md:p-5 hover:bg-muted/30 grid grid-cols-12 gap-3 items-center">
            <div className="col-span-12 md:col-span-4">
              <div className="serif text-lg">{a.full_name}</div>
              <div className="text-xs text-muted-foreground">{a.email}</div>
            </div>
            <div className="col-span-6 md:col-span-3 text-sm text-muted-foreground">{a.region ?? ""}</div>
            <div className="col-span-6 md:col-span-3 text-xs text-muted-foreground">{(a.themes ?? []).join(", ") || ""}</div>
            <div className="col-span-12 md:col-span-2 text-right">
              <StatusBadge status={a.status} />
            </div>
          </button>
        ))}
      </div>

      {open && <ApplicationDrawer kind={kind} item={open} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls =
    status === "approved" ? "bg-foreground text-background" :
    status === "rejected" ? "border border-destructive text-destructive" :
    "border border-border text-muted-foreground";
  return <span className={`eyebrow px-2 py-1 ${cls}`}>{status}</span>;
}

function ApplicationDrawer({ kind, item, onClose, onSaved }: { kind: Kind; item: App; onClose: () => void; onSaved: () => void }) {
  const [note, setNote] = useState(item.admin_note ?? "");
  const [busy, setBusy] = useState<ApplicationStatus | null>(null);
  async function act(status: ApplicationStatus) {
    setBusy(status);
    try {
      if (kind === "visagist") await updateVisagistApplication(item.id, status, note || null);
      else await updateApplication(item.id, status, note || null);
      onSaved();
    }
    finally { setBusy(null); }
  }
  return (
    <div className="fixed inset-0 z-50 bg-foreground/40 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-background border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 md:p-8">
        <div className="flex justify-between items-start gap-4">
          <div>
            <div className="eyebrow text-muted-foreground">Bewerbung</div>
            <h2 className="serif text-3xl mt-2">{item.full_name}</h2>
            <div className="text-sm text-muted-foreground mt-1">{item.email} · {item.region ?? ""}</div>
          </div>
          <button onClick={onClose} className="eyebrow">Close</button>
        </div>
        <dl className="mt-6 space-y-4 text-sm">
          <Row label="Land / Stadt">{[item.country, item.city].filter(Boolean).join(", ") || item.region || ""}</Row>
          <Row label="Preis ab">{item.price_from != null ? `${item.price_from.toLocaleString("de-DE")} €` : ""}</Row>
          <Row label="Preisoptionen">{[item.tfp ? "TFP" : null, item.price_on_request ? "Auf Anfrage" : null].filter(Boolean).join(" · ")}</Row>
          <Row label="Website">{item.website_url ? <a className="border-b border-foreground" href={item.website_url} target="_blank" rel="noreferrer">{item.website_url}</a> : ""}</Row>
          <Row label="Instagram">{item.instagram_handle ? <a className="border-b border-foreground" href={`https://instagram.com/${item.instagram_handle}`} target="_blank" rel="noreferrer">@{item.instagram_handle}</a> : ""}</Row>
          <Row label="Themen">{(item.themes ?? []).join(", ") || ""}</Row>
          <Row label="Portfolio">{item.portfolio_url ? <a className="border-b border-foreground" href={item.portfolio_url} target="_blank" rel="noreferrer">{item.portfolio_url}</a> : ""}</Row>
          <Row label="Bio"><div className="whitespace-pre-wrap">{item.bio ?? ""}</div></Row>
          <Row label="Profilbild">
            {(item as { portrait_url?: string | null }).portrait_url ? (
              <img src={(item as { portrait_url?: string | null }).portrait_url!} alt="" className="w-24 h-24 object-cover border border-border" />
            ) : (
              <span className="text-muted-foreground">Keines</span>
            )}
          </Row>
          <Row label="Sample Bilder">
            {(item.sample_image_urls ?? []).length === 0 ? (
              <span className="text-muted-foreground">Keine</span>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {(item.sample_image_urls ?? []).map((url, i) => (
                  <SampleImage key={i} url={url} index={i} />
                ))}
              </div>
            )}
          </Row>
          <Row label="Eingereicht">{new Date(item.created_at).toLocaleString()}</Row>
          <Row label="Status"><StatusBadge status={item.status} /></Row>
        </dl>
        <div className="mt-6">
          <div className="eyebrow text-muted-foreground mb-2">Notiz / Absage-Text</div>
          <p className="text-xs text-muted-foreground mb-2">
            Wird intern gespeichert. Beim Ablehnen wird dieser Text zusätzlich als Absage-E-Mail
            an {item.email} gesendet (leer = neutrale Standard-Absage).
          </p>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
            className="w-full border border-border bg-transparent p-3 text-sm" maxLength={2000} />
        </div>
        <div className="mt-6 flex flex-wrap gap-2">
          <button disabled={!!busy} onClick={() => act("approved")} className="eyebrow bg-foreground text-background px-4 py-2 disabled:opacity-50">{busy === "approved" ? "…" : "Approve"}</button>
          <button disabled={!!busy} onClick={() => act("rejected")} className="eyebrow border border-destructive text-destructive px-4 py-2 disabled:opacity-50">{busy === "rejected" ? "…" : "Reject"}</button>
          <button disabled={!!busy} onClick={() => act("pending")} className="eyebrow border border-border px-4 py-2 disabled:opacity-50">Auf Pending</button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-4">
      <dt className="eyebrow text-muted-foreground col-span-1">{label}</dt>
      <dd className="col-span-2">{children}</dd>
    </div>
  );
}

async function downloadAsJpeg(url: string, filename: string): Promise<void> {
  const img = new Image();
  img.crossOrigin = "anonymous";
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("Bild konnte nicht geladen werden"));
    img.src = url;
  });
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas nicht verfügbar");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92)
  );
  if (!blob) throw new Error("Konvertierung fehlgeschlagen");
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

function SampleImage({ url, index }: { url: string; index: number }) {
  const [state, setState] = useState<"idle" | "loading" | "error">("idle");
  async function onDownload() {
    setState("loading");
    try {
      await downloadAsJpeg(url, `sample-${index + 1}.jpg`);
      setState("idle");
    } catch {
      setState("error");
    }
  }
  return (
    <div className="relative group border border-border bg-muted">
      <a href={url} target="_blank" rel="noreferrer" className="block aspect-square overflow-hidden">
        <SmartImg source={url} transform={{ width: 240, resize: "contain" }} alt={`Sample ${index + 1}`} className="w-full h-full object-contain" loading="lazy" decoding="async" />
      </a>
      <button
        type="button"
        onClick={onDownload}
        disabled={state === "loading"}
        className="absolute bottom-0 inset-x-0 eyebrow text-[10px] bg-foreground/80 text-background py-1 hover:bg-foreground disabled:opacity-60"
      >
        {state === "loading" ? "…" : state === "error" ? "Fehler" : "JPEG ↓"}
      </button>
    </div>
  );
}